import { Box, Button, Dialog, DialogContent, Stack, Tooltip, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import CloseIcon from '../../../../assets/images/close-icon.svg?react'
import { useGetMonitoringCertificateFn, useTriggerSendingOfCertificateEmailFn } from '../../../../api/apiQueries'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { useAuthenticationContext } from '../../../../auth/AuthenticationContextProvider'
import { WRITE_MONITORING_CERTIFICATE_SCOPE } from '../../../../constants'
import { Location } from '../../../../api/models'
import { isProfessionallyMonitored } from './locationUtils'
import { LoggerName, namedLogger } from '../../../../utils/logger'

const logger = namedLogger(LoggerName.MonitoringCertificate)

export const MonitoringCertificate: FC<{ location: Location }> = ({ location }) => {
  const authContext = useAuthenticationContext()

  const { toast } = useSnackbar()
  const triggerSendingOfCertificateEmail = useTriggerSendingOfCertificateEmailFn()
  const getMonitoringCertificate = useGetMonitoringCertificateFn()
  const primaryEmailAddress = location.user.emailAddress || ''

  const [viewCertificateButtonLoading, setViewCertificateButtonLoading] = useState(false)
  const [certificateDialogIsOpen, setCertificateDialogIsOpen] = useState(false)
  const [emailSendButtonLoading, setEmailButtonDisabled] = useState(false)
  const [monitoringCertificateUrl, setMonitoringCertificateUrl] = useState<string | null>(null)

  const handleViewCertificate = async () => {
    if (!monitoringCertificateUrl) {
      try {
        setViewCertificateButtonLoading(true)
        const monitoringCertificateBlob = await getMonitoringCertificate(location.id)

        // even though this is baseline functionality, I'm checking for URL.createObjectURL because it is not implemented in jsdom and will break the tests
        if (URL.createObjectURL) {
          const url = URL.createObjectURL(monitoringCertificateBlob)
          setMonitoringCertificateUrl(url)
        }

        setCertificateDialogIsOpen(true)
      } catch {
        toast('Monitoring certificate is not available.', { variant: 'error' })
      } finally {
        setViewCertificateButtonLoading(false)
      }
    } else {
      setCertificateDialogIsOpen(true)
    }
  }

  // when we unmount, we need to clean up the object URL to avoid memory leaks
  useEffect(() => {
    return () => {
      if (monitoringCertificateUrl) {
        // even though this is baseline functionality, I'm checking for URL.revokeObjectURL because it is not implemented in jsdom and will break the tests
        if (URL.revokeObjectURL) {
          URL.revokeObjectURL(monitoringCertificateUrl)
        }
      }
    }
  }, [monitoringCertificateUrl])

  const emailCertificateToPrimaryEmail = async () => {
    try {
      setEmailButtonDisabled(true)
      await triggerSendingOfCertificateEmail(location.id, { emailAddresses: [primaryEmailAddress] })
      toast('Monitoring certificate email sent successfully!', { variant: 'success' })
    } catch (error) {
      logger.error('Error sending certificate email', error as Error)
      toast('Failed to send monitoring certificate email. Please try again.', { variant: 'error' })
    } finally {
      setEmailButtonDisabled(false)
    }
  }

  if (!isProfessionallyMonitored(location)) {
    return null
  }

  return (
    <Stack data-testid={'monitoring-certificate'}>
      <Stack sx={{ position: 'relative' }}>
        <Stack spacing={'var(--spacing-l)'} sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              bgcolor: 'var(--bg-surface)',
              borderRadius: 'var(--border-radius-l)',
              p: 'var(--spacing-m)'
            }}
          >
            <Stack>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 'var(--spacing-s)', alignItems: 'center' }}>
                  <Typography variant="h2" fontSize={'var(--size-xl)'} fontWeight="700" color="text.primary">
                    Monitoring Certificate
                  </Typography>
                </Box>
              </Box>
              <Stack sx={{ marginTop: 'var(--spacing-xs)' }}>
                <Typography sx={{ width: '22.563rem' }} variant="body1" fontSize={'var(--size-s)'} fontWeight="400" color="text.primary">
                  View the customer’s monitoring certificate or send it to the primary email.
                </Typography>
              </Stack>
            </Stack>
            <Tooltip
              title={
                !authContext.has(WRITE_MONITORING_CERTIFICATE_SCOPE) ? 'You do not have permission to view or email the monitoring certificate.' : ''
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-m)' }}>
                <Button
                  disabled={!authContext.has(WRITE_MONITORING_CERTIFICATE_SCOPE)}
                  loading={viewCertificateButtonLoading}
                  onClick={handleViewCertificate}
                  data-testid={'view-certificate-button'}
                >
                  View Certificate
                </Button>
                <Button
                  disabled={!authContext.has(WRITE_MONITORING_CERTIFICATE_SCOPE)}
                  loading={emailSendButtonLoading}
                  onClick={() => {
                    emailCertificateToPrimaryEmail()
                  }}
                  data-testid={'email-certificate-button'}
                >
                  Email Certificate
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Stack>
      </Stack>
      <Dialog open={certificateDialogIsOpen} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            onClick={() => setCertificateDialogIsOpen(false)}
            data-testid={'certificate-close-button'}
            sx={{ position: 'absolute', top: 'var(--spacing-2xs)', right: 'var(--spacing-2xs)', cursor: 'pointer' }}
          >
            <CloseIcon />
          </Box>
          <Box sx={{ width: '100%', height: '56.25rem', mt: 'var(--spacing-m)' }}>
            {monitoringCertificateUrl && (
              <iframe
                data-testid={'certificate-iframe'}
                src={monitoringCertificateUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Monitoring Certificate PDF"
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Stack>
  )
}
